import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from '../contexts/AuthContext';
import type { CredentialKind, LabApi, ReadMode } from '../utils/middlemanRules';
import { responseDetail } from '../utils/responseDetail';
import { useApiFetch } from './useApiFetch';
import { useApiQuery } from './useApiQuery';

/** The Hawk API's admin-only pass-through to middleman's `/admin` routes. */
export const PROXY_ADMIN_BASE = '/middleman_admin';

export type ProxyStatus = 'active' | 'inactive';

export interface ReadPolicy {
  mode: ReadMode;
  tags: string[];
  zdr_only: boolean;
}

/** A database-configured model, as middleman's `ModelConfigResponse` reports it. */
export interface ProxyModel {
  public_name: string;
  group: string;
  lab: string;
  danger_name: string | null;
  are_details_secret: boolean;
  dead: boolean;
  is_zdr: boolean;
  /** Null: nobody has recorded whether it returns raw chain-of-thought. */
  raw_cot: boolean | null;
  /** Null: nobody has recorded which company serves it. */
  provider: string | null;
  override_url: string | null;
  force_tokenizer: string | null;
  concurrency_limit: number | null;
  context_length: number | null;
  output_limit: number | null;
  lab_documentation_url: string | null;
  vision: boolean;
  comments: string | null;
  max_tokens_keyword: string;
  // Decimals, which the API may serialize as strings.
  input_cost_per_1m: number | string | null;
  output_cost_per_1m: number | string | null;
  cache_read_input_cost_per_1m: number | string | null;
  cache_write_input_cost_per_1m: number | string | null;
  base_model_info_key: string | null;
  private_name: string | null;
  limits: {
    RPM?: number | null;
    TPM?: number | null;
    TPD?: number | null;
  } | null;
  request_timeout_minutes: number;
  stream: boolean;
  return_request_id: boolean | null;
  anthropic_account: string | null;
  openai_account: string | null;
  required_anthropic_betas: string[] | null;
  required_anthropic_betas_error?: string | null;
  read_policy: ReadPolicy;
  read_policy_error?: string | null;
  reader_tags: string[];
  reader_tags_error?: string | null;
}

/** A model row with which list it came from (the API's rows don't say). */
export interface ListedModel extends ProxyModel {
  is_active: boolean;
}

export type { CredentialKind, LabApi } from '../utils/middlemanRules';
export { PROVIDERS } from '../utils/middlemanRules';

export interface ProxyLab {
  name: string;
  api: LabApi;
  base_url: string | null;
  credential_kind: CredentialKind | null;
  api_key_name: string | null;
  high_priority_api_key_name: string | null;
  unsupported_paths: string[];
  provider: string | null;
  is_chat: boolean;
  is_active: boolean;
}

/** How each provider's last model discovery went (middleman's `FetchStatus`). */
export type FetchStatus =
  'not_run' | 'ok' | 'cached' | 'failed' | 'no_key' | 'stale';

export interface DiscoveryStatus {
  model_count: number;
  discovery: Record<string, FetchStatus>;
}

export interface ReloadResult extends DiscoveryStatus {
  status: string;
}

/**
 * Whether a name has a `.` or `..` path segment. No URL can address it: the
 * browser resolves such segments (percent-encoded ones too) before sending, so
 * `org/../example-model` would reach `example-model` instead.
 */
export const hasDotSegment = (name: string) =>
  name.split('/').some(segment => segment === '.' || segment === '..');

/** Model names may contain `/`, which the route keeps as path segments. */
export const modelPath = (name: string, action?: 'activate' | 'deactivate') =>
  `${PROXY_ADMIN_BASE}/models/${name.split('/').map(encodeURIComponent).join('/')}${action ? `/${action}` : ''}`;

export const labPath = (name: string, action?: 'activate' | 'deactivate') =>
  `${PROXY_ADMIN_BASE}/labs/${encodeURIComponent(name)}${action ? `/${action}` : ''}`;

export const useProxyModels = (status: ProxyStatus, enabled = true) =>
  useApiQuery<ProxyModel[]>(
    `${PROXY_ADMIN_BASE}/models/?status=${status}`,
    enabled
  );

export const useProxyLabs = (status: ProxyStatus | 'all') =>
  useApiQuery<ProxyLab[]>(`${PROXY_ADMIN_BASE}/labs/?status=${status}`);

export const useProxyDiscovery = () =>
  useApiQuery<DiscoveryStatus>(`${PROXY_ADMIN_BASE}/models/discovery`);

/** `checking` until known; `unknown` when the proxy couldn't answer (e.g. middleman down). */
export type ProxyAccess = 'checking' | 'admin' | 'denied' | 'unknown';

const ACCESS_PROBE = `${PROXY_ADMIN_BASE}/models/discovery`;

/**
 * Whether middleman counts the caller as an admin. Middleman decides, as it does
 * for `hawk proxy`, and its rules (a dev-admin claim, admin groups) differ from
 * Hawk's own admin flag, so probe its cheapest admin route rather than guess.
 */
export function useProxyAdminAccess(): {
  access: ProxyAccess;
  /** Check again, as after a failed check. */
  retry: () => void;
} {
  const { apiFetch } = useApiFetch();
  const { user } = useAuthContext();
  const caller = user?.sub ?? null;
  const query = useQuery<'admin' | 'denied'>({
    // Its own key: the discovery query caches the body, this the verdict.
    queryKey: ['api', caller, ACCESS_PROBE, 'access'],
    enabled: caller !== null,
    // Only a verdict is kept this long; a failed check isn't one, so it's
    // rechecked on the next visit or by "Try again".
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async ({ signal }) => {
      const res = await apiFetch(
        ACCESS_PROBE,
        { signal },
        { returnNonOkResponse: true }
      );
      if (res?.ok) return 'admin';
      if (res?.status === 403) return 'denied';
      // Thrown, not returned: a failed check must not be cached as "no access"
      // for five minutes, and on an error the previous verdict stays.
      throw new Error(
        res ? `access check failed (HTTP ${res.status})` : 'access check failed'
      );
    },
  });
  return {
    access: query.data ?? (query.isError ? 'unknown' : 'checking'),
    retry: () => void query.refetch(),
  };
}

/**
 * Send one write to the proxy admin API. The API checks admin on every call;
 * a refusal shows middleman's own reason (a lab in use, an unknown lab).
 * Resolves to the response body, or null when refused.
 */
export function useProxyWrite() {
  const { apiFetch } = useApiFetch();
  const { user } = useAuthContext();
  const client = useQueryClient();
  const caller = user?.sub ?? null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = useCallback(
    async (method: string, url: string, body?: unknown): Promise<unknown> => {
      // The last line of defence: the browser would resolve a dot segment and
      // send the write to a different model or lab.
      if (hasDotSegment(url.split('?')[0])) {
        setError(
          "This name has a '.' or '..' segment, which no URL can address; manage it with `hawk proxy`."
        );
        return null;
      }
      setBusy(true);
      setError(null);
      // Null on a network error or abort; apiFetch does not reject.
      const res = await apiFetch(
        url,
        {
          method,
          headers:
            body === undefined
              ? undefined
              : { 'Content-Type': 'application/json' },
          body: body === undefined ? undefined : JSON.stringify(body),
        },
        { returnNonOkResponse: true }
      );
      if (!res?.ok) {
        setError(res ? await responseDetail(res) : 'Could not reach the API.');
        setBusy(false);
        return null;
      }
      let result: unknown = {};
      try {
        result = await res.json();
      } catch {
        // Applied, but the reply was cut off; the refetch shows the outcome.
      }
      // Models, labs and discovery all read the same configuration; refetch it.
      void client.invalidateQueries({
        predicate: query =>
          query.queryKey[0] === 'api' &&
          query.queryKey[1] === caller &&
          String(query.queryKey[2]).startsWith(PROXY_ADMIN_BASE),
      });
      setBusy(false);
      return result;
    },
    [apiFetch, client, caller]
  );

  const reset = useCallback(() => {
    setBusy(false);
    setError(null);
  }, []);
  return { busy, error, send, reset };
}
